import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { RewardType } from '@sorare/core/src/lib/rewards';

import { LazyBoxOverviewDialog } from 'components/rewards/LazyBoxOverviewDialog';
import { RewardOverview } from 'components/rewards/RewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';

import type { TaskRewardBlock_anyRewardConfigInterface } from './__generated__/index.graphql';

type Props = {
  taskRewards: Nullable<TaskRewardBlock_anyRewardConfigInterface[]>;
};

const Content = styled(Vertical)`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;

export const TaskRewardBlock = ({ taskRewards }: Props) => {
  const [openingBundleConfigId, setOpeningBundleConfigId] = useState<string>();

  const bundleRewards = taskRewards?.filter(
    isTypeFn('ProbabilisticBundleRewardConfig')
  );

  if (!bundleRewards || bundleRewards.length === 0) {
    return null;
  }

  return (
    <>
      <Vertical>
        <LabelM bold color="var(--c-nd-600)">
          <FormattedMessage {...fantasy.boxes} />
        </LabelM>
        <Content>
          {bundleRewards.map(bundleReward => (
            <ButtonBase
              key={bundleReward.probabilisticBundleConfig.id}
              onClick={() => {
                setOpeningBundleConfigId(
                  bundleReward.probabilisticBundleConfig.id
                );
              }}
            >
              <RewardOverview
                variant={Variant.INLINE}
                simpleReward={{
                  type: RewardType.PROBABILISTIC_BUNDLE,
                  rarity: bundleReward.probabilisticBundleConfig.rarity,
                  count: 1,
                }}
              />
            </ButtonBase>
          ))}
        </Content>
      </Vertical>
      {!!openingBundleConfigId && (
        <LazyBoxOverviewDialog
          open
          onClose={() => setOpeningBundleConfigId(undefined)}
          boxConfigId={openingBundleConfigId}
        />
      )}
    </>
  );
};

TaskRewardBlock.fragments = {
  anyRewardConfigInterface: gql`
    fragment TaskRewardBlock_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on ProbabilisticBundleRewardConfig {
        id
        probabilisticBundleConfig {
          id
          rarity
        }
      }
    }
  ` as TypedDocumentNode<TaskRewardBlock_anyRewardConfigInterface>,
};
